import { FunctionComponent } from "react";
import { Box, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { nb } from "date-fns/locale";
import { format } from "date-fns";

import { BookingDTO, CustomerBooking } from "../../../types/bookingTypes";
import BikeboxLogo from "../../../static/bikebox-logo.png";

export type Props = {
  booking: CustomerBooking | BookingDTO;
  promoCode: string | null;
};

function isBookingDTO(
  booking: CustomerBooking | BookingDTO
): booking is BookingDTO {
  return (booking as BookingDTO).id !== undefined;
}

const BookingInfoTables: FunctionComponent<Props> = ({
  booking,
  promoCode,
}) => {
  const isCustomerBooking = !isBookingDTO(booking);

  const tableSxStyles = {
    border: "1px solid #e2e2e2",
    tableLayout: "fixed",
    "& td": {
      wordWrap: "break-word",
      overflowWrap: "break-word",
    },
  } as const;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        "@media (max-width:960px)": {
          gridTemplateColumns: "1fr",
        },
      }}
    >
      <Table
        sx={tableSxStyles}
      >
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography variant="body1">Navn:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1">
                {isBookingDTO(booking)
                  ? booking.customerName
                  : booking.firstname.concat(" ", booking.surname)}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="body1">Mobilnummer:</Typography>
            </TableCell>
            <TableCell>
              {isCustomerBooking ? (
                <Typography variant="body1">
                  {`(${booking.customerPhone.countryCode}) ${booking.customerPhone.nationalNumber}`}
                </Typography>
              ) : (
                <Typography>{booking.customerPhone}</Typography>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Typography variant="body1">Email:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1">{booking.customerEmail}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table sx={tableSxStyles}>
        <TableBody>
          {isCustomerBooking && (
            <TableRow>
              <TableCell>
                <Typography variant="body1">Butikk:</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">
                  {booking.storeOption.name}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              <Typography variant="body1">Dato:</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body1">
                {booking.deliveryDate
                  ? format(
                      new Date(booking.deliveryDate),
                      "eeee do LLLL, yyyy",
                      {
                        locale: nb,
                      }
                    )
                  : "---"}
              </Typography>
            </TableCell>
          </TableRow>
          {promoCode && (
            <TableRow>
              <TableCell>
                <Typography variant="body1">Kampanjekode:</Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body1"
                  style={{ textTransform: "uppercase" }}
                >
                  {promoCode}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {isCustomerBooking && (
            <TableRow>
              <TableCell>
                <Typography variant="body1">Antall:</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body1">
                  {booking.bookingElements.length}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {isCustomerBooking ? (
            booking.bookingElements.map((bookingElement, i) => {
              return (
                <TableRow key={bookingElement.id}>
                  <TableCell>
                    <Typography variant="body1">
                      <Box 
                        component='span'
                        sx={{ fontWeight: 500, color: "#9c9c9c" }}
                      >
                        {i + 1} –{" "}
                      </Box>{" "}
                      {bookingElement.brand?.name}
                    </Typography>
                    {bookingElement.bikebox && (
                      <img height={35} src={BikeboxLogo} alt="Bikebox logo" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {bookingElement.description}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">Sykkelmerke:</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">{booking.brand.name}</Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">Beskrivelse:</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    {booking.description === "" ? (
                      <i>Ingen beskrivelse</i>
                    ) : (
                      booking.description
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default BookingInfoTables;
